import type { Exchange, Operation, CombinedError, OperationResult } from '@urql/core';
import { makeOperation } from '@urql/core';
import type { Source } from 'wonka';
import {
    makeSubject,
    concatMap,
    share,
    pipe,
    map,
    merge,
    filter,
    fromValue,
    mergeMap,
    takeUntil,
    fromPromise,
} from 'wonka';

import type { IGraphController } from '../graphql';

export interface MFAExchangeOptions {
    maxNumberAttempts?: number;
    authController: IGraphController;
}

function getMfaError(error?: CombinedError) {
    if (!error) {
        return;
    }

    return error.graphQLErrors.find((e) => {
        const extension = e.extensions as { type: string; acrValues?: string } | undefined;
        return extension?.type === 'MFA_REQUIRED' && extension?.acrValues;
    }) as { extensions: { type: string; acrValues?: string } } | undefined;
}

export const mfaExchange = ({
    maxNumberAttempts,
    authController,
}: MFAExchangeOptions): Exchange => {
    const MAX_ATTEMPTS = maxNumberAttempts || 2;
    let latestAcrValues: string | undefined;

    return ({ forward, dispatchDebug }) =>
        (ops$) => {
            const sharedOps$ = pipe(ops$, share);
            const { source: retry$, next: nextRetryOperation } = makeSubject<Operation>();

            const retryWithBackoff$ = pipe(
                retry$,
                mergeMap((op: Operation) => {
                    const { key, context } = op;
                    const retryCount = (context.retryCount || 0) + 1;

                    // We stop the retries if a teardown event for this operation comes in
                    // But if this event comes through regularly we also stop the retries, since it's
                    // basically the query retrying itself, no backoff should be added!
                    const teardown$ = pipe(
                        sharedOps$,
                        filter((op) => {
                            return (
                                (op.kind === 'query' || op.kind === 'teardown') && op.key === key
                            );
                        }),
                    );

                    dispatchDebug({
                        type: 'retryAttempt',
                        message: `The operation has failed and a retry has been triggered (${retryCount} / ${MAX_ATTEMPTS})`,
                        operation: op,
                        data: {
                            retryCount,
                        },
                    });

                    // Add new retryDelay and retryCount to operation
                    return pipe(
                        fromValue(
                            makeOperation(op.kind, op, {
                                ...op.context,
                                retryCount,
                            }),
                        ),

                        concatMap((val) => {
                            return pipe(
                                fromPromise(
                                    authController.login({
                                        acrValues: latestAcrValues,
                                    }),
                                ),
                                map(() => {
                                    return val;
                                }),
                            );
                        }),
                        // Stop retry if a teardown comes in
                        takeUntil(teardown$),
                    );
                }),
            );

            const result$ = pipe(
                merge([sharedOps$, retryWithBackoff$]),
                forward,
                share,
                filter((res) => {
                    const mfaError = getMfaError(res.error);

                    // Only retry if the error passes the conditional retryIf function (if passed)
                    // or if the error contains a networkError
                    if (!res.error || !mfaError) {
                        return true;
                    }

                    const maxNumberAttemptsExceeded =
                        (res.operation.context.retryCount || 0) >= MAX_ATTEMPTS - 1;

                    if (!maxNumberAttemptsExceeded) {
                        const operation = res.operation;
                        if (!operation) return true;

                        latestAcrValues = mfaError.extensions.acrValues;

                        // Send failed responses to be retried by calling next on the retry$ subject
                        // Exclude operations that have been retried more than the specified max
                        nextRetryOperation(operation);
                        return false;
                    }

                    dispatchDebug({
                        type: 'retryExhausted',
                        message:
                            'Maximum number of retries has been reached. No further retries will be performed.',
                        operation: res.operation,
                    });

                    return true;
                }),
            ) as Source<OperationResult>;

            return result$;
        };
};
